// Ported from relay-protocol packages/relay-widget (v0.1.0).
// Minimal viem ABIs for the permissionless self-recovery flow (Task 2).
// Verbatim-verified against the compiled artifacts in
// contracts/out/StandingForwarderFactory.sol/StandingForwarderFactory.json
// and contracts/out/StandingForwarder.sol/StandingForwarder.json -- do NOT
// hand-edit signatures here without re-checking those artifacts, a mismatch
// would silently encode the wrong calldata.
//
// Kept intentionally tiny (only the 3 functions the recovery flow calls) --
// this is not a general-purpose contract ABI, just enough for viem/wagmi's
// `writeContract` to type-check `functionName`/`args` correctly.

/** StandingForwarderFactory.deploy(address recipient, uint256 version) returns (address) --
 * mints (deterministically, per recipient+version) the standing forwarder contract for a
 * user who hasn't deployed theirs yet. Permissionless: any address can call this for any
 * recipient, and the deployed forwarder always resolves to the same CREATE2 address. */
export const standingForwarderFactoryAbi = [
  {
    type: 'function',
    name: 'deploy',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'recipient', type: 'address' },
      { name: 'version', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'address' }],
  },
] as const

/** StandingForwarder.execute() and .sweepToUser(address tokenAddr) -- both permissionless:
 * neither takes a destination argument, so funds always route to the forwarder's immutable
 * recipient. `execute()` completes the normal bridge delivery for whatever's currently held;
 * `sweepToUser(tokenAddr)` recovers a stuck ERC-20 balance directly to the recipient instead. */
export const standingForwarderAbi = [
  {
    type: 'function',
    name: 'execute',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: [],
  },
  {
    type: 'function',
    name: 'sweepToUser',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'tokenAddr', type: 'address' }],
    outputs: [],
  },
] as const

/** StandingForwarderFactory / DepositorForwarderFactory
 * `computeAddress(address recipient, uint256 version) view returns (address)`.
 *
 * Both factories expose this with an identical signature (verified against the
 * live BSC contracts), which is what lets one ABI verify either kind. This is
 * the factory's OWN derivation -- the same `Create2.computeAddress(_salt(...),
 * keccak256(_initCode(...)))` its `deploy()` uses -- so it cannot disagree with
 * where the factory actually deploys. See verifyForwarder.ts for why the check
 * is anchored here rather than on a locally embedded creation code. */
export const forwarderFactoryComputeAbi = [
  {
    type: 'function',
    name: 'computeAddress',
    stateMutability: 'view',
    inputs: [
      { name: 'recipient', type: 'address' },
      { name: 'version', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'address' }],
  },
] as const
